import type { FC, MouseEvent } from "react";

import classNames from "classnames";

import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";
import { SearchDefaultIcon } from "@jti/brand-icons/search-default";
import { UiEarthOutlineIcon } from "@jti/brand-icons/ui-earth-outline";
import { UiLogoutIcon } from "@jti/brand-icons/ui-logout";
import { Separator } from "@jti/core-components/components/Separator";

import type { MobileNavToolRow } from "../../types";
import { HeaderMiniCart } from "../HeaderMiniCart";
import { HeaderNavListItem } from "../HeaderNavListItem";

import styles from "../../MobileNavigation.module.scss";

type ToolsPanelProps = {
  toolsLinks: MobileNavToolRow[];
  activePanelId: string;
  languagePanelId: string;
  openPanel: (panelId: string) => (event: MouseEvent<HTMLButtonElement>) => void;
  isUserLoggedIn: boolean;
  logoutLabel: string;
  onLogOut: () => void;
  isLogOutPending: boolean;
  languageLabel: string;
  searchLabel?: string;
  isSearchOpened?: boolean;
  onOpenSearch?: () => void;
  onCloseSearch?: () => void;
  cartLabel?: string;
  cartItemsCount?: number;
  onCartOpen?: () => void;
  showLanguageSelector: boolean;
};

export const ToolsPanel: FC<ToolsPanelProps> = ({
  toolsLinks,
  activePanelId,
  languagePanelId,
  openPanel,
  isUserLoggedIn,
  logoutLabel,
  onLogOut,
  isLogOutPending,
  languageLabel,
  searchLabel,
  isSearchOpened,
  onOpenSearch,
  onCloseSearch,
  cartLabel,
  cartItemsCount,
  onCartOpen,
  showLanguageSelector,
}) => (
  <>
    <div className={styles.separator}>
      <Separator />
    </div>
    <ul className={classNames(styles.list, styles.tools)}>
      {(onOpenSearch || onCloseSearch) && searchLabel && (
        <li>
          <HeaderNavListItem
            label={searchLabel}
            disabled={isSearchOpened}
            startIcon={SearchDefaultIcon}
            onClick={onOpenSearch}
            isCondensed={true}
          />
        </li>
      )}
      {onCartOpen && cartLabel && (
        <li>
          <HeaderMiniCart label={cartLabel} count={cartItemsCount} onClick={onCartOpen} />
        </li>
      )}
      {toolsLinks.map(({ id: toolId, label, href, onClick, icon, badge }) => {
        const displayLabel = typeof badge === "number" && badge > 0 ? `${label} (${badge})` : label;

        return href ? (
          <li key={toolId}>
            <HeaderNavListItem variant="link" label={displayLabel} href={href} startIcon={icon} isCondensed={true} />
          </li>
        ) : (
          <li key={toolId}>
            <HeaderNavListItem label={displayLabel} startIcon={icon} onClick={() => onClick?.()} isCondensed={true} />
          </li>
        );
      })}
      {showLanguageSelector && (
        <li>
          <HeaderNavListItem
            label={languageLabel}
            startIcon={UiEarthOutlineIcon}
            endIcon={ChevronForwardIcon}
            aria-haspopup="true"
            aria-expanded={activePanelId === languagePanelId}
            aria-controls={languagePanelId}
            onClick={openPanel(languagePanelId)}
            isCondensed={true}
          />
        </li>
      )}
    </ul>
    {isUserLoggedIn && (
      <>
        <div className={styles.separator}>
          <Separator />
        </div>
        <ul className={styles.list}>
          <li>
            <HeaderNavListItem
              label={logoutLabel}
              startIcon={UiLogoutIcon}
              onClick={onLogOut}
              disabled={isLogOutPending}
              isCondensed={true}
            />
          </li>
        </ul>
      </>
    )}
  </>
);
